import React, { useState } from 'react'
import Menu, { MenuProps } from 'components/menu'
import { addNewView, saveCurrentView } from 'components/spreadsheet/contexts/data/actions'
import { useApplicationStore } from 'hooks/application'
import { useProject } from 'hooks/useProject'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import Button from 'components/button'
import Tooltip from 'components/tooltip'
import { useNavigate, generatePath } from 'react-router'
import { TABLE } from 'routes'
import { useAuth } from 'hooks/auth'
import usePersonalSaveBlocked, { getSaveBlockedTooltip } from 'components/spreadsheet/hooks/usePersonalSaveBlocked'
import { hasUnsavedChanges } from 'components/spreadsheet/helpers/customViewLatest'
import { ViewTypes } from 'components/spreadsheet/constants/const'

const SaveMenu: React.FC<MenuProps> = ({ id, menuState, setMenuState, width }) => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const { project } = useProject()
  const { spreadsheetData, setSpreadsheetData, inProcess, originalViewId } = useDataContext()
  const [isSavingNewView, setIsSavingNewView] = useState(false)
  const isCustomView = spreadsheetData.viewDetails.type === ViewTypes.CUSTOM_VIEW
  const unsaved = hasUnsavedChanges(spreadsheetData)

  const successfulViewSave = () => {
    setSnackbarMessage({ status: 'success', message: 'The view has been saved!' })
    setMenuState(INITIAL_CONTEXT_MENU_STATE)
  }

  const unsuccessfulViewSave = (error: any) => {
    displayErrorMessage(error)
  }

  const handleViewSave = () => {
    saveCurrentView(project.publicId, spreadsheetData, setSpreadsheetData, successfulViewSave, unsuccessfulViewSave)
  }

  const handleSaveAsNewView = () => {
    if (isSavingNewView) return
    setIsSavingNewView(true)

    const configurationParentId = inProcess && originalViewId ? originalViewId : null

    addNewView(
      project.publicId,
      spreadsheetData,
      setSpreadsheetData,
      spreadsheetData.viewDetails.type,
      configurationParentId,
      (newViewId: string) => {
        if (!inProcess) {
          navigate(
            generatePath(TABLE, {
              projectId: project.publicId,
              tableId: spreadsheetData.tableDetails.publicId,
              viewId: newViewId
            }),
            { replace: true }
          )
        }
        setSnackbarMessage({
          status: 'success',
          message: `The view has been saved as a new ${inProcess ? 'personal ' : ''}view.`
        })
        setMenuState(INITIAL_CONTEXT_MENU_STATE)
      },
      (error: any) => {
        displayErrorMessage(error)
      },
      inProcess,
      () => {
        setIsSavingNewView(false)
      }
    )
  }

  const showSaveView =
    spreadsheetData.isAdmin ||
    (spreadsheetData.viewDetails.isPersonalView &&
      spreadsheetData.viewDetails.personalViewOwner?.publicId === user?.publicId)

  const personalSaveBlocked = usePersonalSaveBlocked()

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width}>
      <div className="flex flex-col p-0 m-2.5 list-none">
        {unsaved && <div className="truncate text-red m-[5px] p-[5px]">There are unsaved changes</div>}

        {!unsaved && <div className="truncate text-dark-green m-[5px] p-[5px]">Everything is up to date</div>}

        {showSaveView &&
          (personalSaveBlocked ? (
            <Tooltip content={getSaveBlockedTooltip(inProcess)}>
              <span className="inline-block">
                <Button
                  internalType="outline"
                  className="w-[175px] h-8 py-0 px-1"
                  data-tour="save-current-view"
                  disabled
                >
                  <span className="ml-2.5">Save This View</span>
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              internalType="outline"
              className="w-[175px] h-8 py-0 px-1"
              data-tour="save-current-view"
              onClick={() => handleViewSave()}
            >
              <span className="ml-2.5">Save This View</span>
            </Button>
          ))}

        {/* A new Custom View starts from the views menu, on its own prompt. */}
        {!isCustomView &&
          (personalSaveBlocked ? (
            <Tooltip content={getSaveBlockedTooltip(inProcess)}>
              <span className="inline-block mt-2.5">
                <Button className="w-[175px] h-8 py-0 px-1" internalType="outline" disabled>
                  <span className="ml-2.5">Save {inProcess ? 'Personal' : 'New'} View</span>
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              className="mt-2.5 w-[175px] h-8 py-0 px-1"
              internalType="outline"
              onClick={handleSaveAsNewView}
              isLoading={isSavingNewView}
              disabled={isSavingNewView}
            >
              <span className="ml-2.5">Save {inProcess ? 'Personal' : 'New'} View</span>
            </Button>
          ))}

        <div className="whitespace-pre-wrap m-[5px] p-[5px]">
          {isCustomView
            ? "This saves the view's sorts, filters and hidden columns for everyone who opens it. Changes to the page itself are published from the bar above the page."
            : 'This will save the current configuration including sorts, filters, grouping, hidden columns, locked columns and column order.'}
        </div>
      </div>
    </Menu>
  )
}

export default SaveMenu
